// ─────────────────────────────────────────────────────────────────────────────
// WP-D1 (RF-157 / CPE-129 / CPE-134) — Gestión de Ganadores del Admin.
//
// Réplica 1:1 de la maqueta (`fundamentales-admin.js:306-346`): toggle del módulo, tres botones,
// barra de filtros, nota ámbar del periodo de edición, tabla de 10 columnas con
// Estado y acciones, y `#ganadores-no-results`.
//
// 🔴 Diferencia deliberada con la maqueta: aquí los filtros y la paginación son de SERVIDOR. La
// maqueta filtra sobre lo que ya tiene cargado (`_ganadoresApplyFilters`), lo cual funciona con tres
// filas de demo y no con el histórico de tres años — y además el alcance por OU/país es autorización,
// no una comodidad de presentación: decidirlo en el navegador sería decidirlo donde no se puede.
//
// El alta manual se retira de la UI (D-10): `POST v1/winners` se conserva porque es la única vía para
// un ganador sin correo, pero el camino de la pantalla es la carga por fichero.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Modal, ModalHeader, ModalBody, ModalFooter, ConfirmModal, InitialsAvatar } from '@/components/common';
import {
  useWinnersSearch,
  useDeleteWinner,
  useConfirmWinner,
  useUpdateWinner,
  useCampaigns,
  getCampaignName,
  winnersApi,
  getRecognitionErrorMessage,
  useRegistrationCatalogs,
  useWinnerBadges,
  useCampaignBadges,
  getBadgeName,
  useRecognitionCatalog,
  useCountryLanguages,
  getFundamentalName,
  useGestionSettings,
  useSaveGestionSettings,
  useCreateCampaignBadge,
  useUpdateCampaignBadge,
  useDeleteCampaignBadge,
} from '@/api/recognition';
import type {
  AdminBadgeDto, RegisterWinnerBody, WinnerEstado, WinnerRowDto, WinnerSearchParams,
} from '@/api/recognition';
import { exportToExcel } from '@/utils/exportExcel';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import { StateBlock } from '../components';
import { fetchNominacionesCrudasPaginado } from '../fetchNominacionesCrudas';
import { resolverFundamentalesConHabitosParaExport } from '../fundamentalesHabitosMatrix';
import { AdminSection, AdminPagerFooter, aniosDisponibles } from './adminShared';
import { AdminCampaignBadgeGrid } from './AdminCampaignBadgeGrid';
import { AdminCampaignBadgeEditorModal } from './AdminCampaignBadgeEditorModal';
import { construirHojaExcelNominaciones } from './nominationsExportMapper';
import { WinnersImportWizardModal } from './winnersImport/WinnersImportWizardModal';

/** Tamaño de página inicial del listado. Coincide con el default del backend. */
const PAGE_SIZE_INICIAL = 20;

/**
 * Tope de página que el servidor acepta (`WinnerSearchFilter.MaxPageSize`, ya documentado más abajo
 * en `exportar`). `AdminPagerFooter` ofrece una opción "Todas" que en el resto del Admin se resuelve
 * del lado del CLIENTE (`usePagination` mapea `pageSize=0` → todos los items ya cargados) — pero aquí
 * la paginación es de SERVIDOR, así que mandarle `pageSize: 0` literal no tiene un contrato conocido
 * (lo más probable es cero filas o un rechazo). "Todas" en esta pantalla significa, en cambio, "el
 * máximo que el servidor permite por página".
 */
const SERVER_MAX_PAGE_SIZE = 200;

/**
 * `pageSize` que de verdad se manda al servidor: `0` es la opción "Todas" de `AdminPagerFooter` y se
 * traduce al tope del servidor en vez de mandarse literal (ver comentario de `SERVER_MAX_PAGE_SIZE`).
 *
 * Función con nombre en vez de un ternario inline en `AdminWinners`: el mismo valor hace falta en dos
 * sitios (los `params` de la búsqueda y el `colorIndex` de cada fila), y un ternario repetido —o
 * elevado al cuerpo del componente para no repetirlo— empuja la complejidad cognitiva del componente
 * por encima del límite del proyecto (15). Aislado aquí, el ternario cuenta para la complejidad de
 * ESTA función (trivial), no para la de `AdminWinners`.
 */
function pageSizeDeServidor(pageSize: number): number {
  return pageSize === 0 ? SERVER_MAX_PAGE_SIZE : pageSize;
}

/*
 * 🔴 SE RETIRÓ la detección de libro binario en el cliente (`esLibroBinario` + las dos firmas).
 *
 * Existía porque el backend solo entendía CSV: había que cazar el `.xlsx` aquí para no mandarle un
 * ZIP y devolverle al usuario un error de parseo. Ahora el backend LEE `.xlsx`, así que ese
 * pre-chequeo rechazaría justo el formato que la maqueta anuncia en el botón.
 *
 * El único caso que sigue sin poder leerse es el `.xls` binario anterior a 2007, y lo detecta el
 * backend por su firma respondiendo qué hacer (guardarlo como `.xlsx`). La garantía no desaparece:
 * se movió al lado que de verdad abre el fichero, con su prueba
 * (`Un_xls_binario_se_rechaza_diciendo_que_lo_guarden_como_xlsx`). Dos comprobaciones del mismo
 * formato en dos lenguajes distintas es exactamente cómo divergen.
 */

const WINNER_EDIT_TITLE_ID = 'adm-winner-edit-title';

/** Forma mínima de un `useMutation` que necesita `insigniaGuardadoErrorMensaje`. */
interface MutacionConError {
  isError: boolean;
  error: unknown;
}

/**
 * Mensaje del intento de crear/editar una insignia de campaña fallido, para pintar DENTRO del modal
 * (ver prop `saveError` de `AdminCampaignBadgeEditorModal`). Vive fuera del componente (función
 * nombrada, no un ternario inline) porque `guardarInsignia` deja el modal abierto a propósito cuando
 * falla el guardado, y el mensaje visible tiene que reflejar la mutación que de verdad se usó
 * (crear o editar, nunca un OR de las tres) para que el error de una no se cuele en la sesión de la
 * otra.
 */
export function insigniaGuardadoErrorMensaje(
  mutacion: MutacionConError,
  fallback: string,
): string | null {
  if (!mutacion.isError) return null;
  return getRecognitionErrorMessage(mutacion.error, fallback);
}

export function AdminWinners() {
  const { t, i18n } = useTranslation('recognition');

  // Matriz de permisos de Reconocimiento — "Exportar datos" (RN-K03) gatea los dos botones de
  // descarga aparte de "Gestionar ganadores", que ya autoriza el resto de la pantalla (RoleGuard de
  // la sección). `?.` en cascada: mientras el perfil resuelve, falla CERRADO (botones ocultos).
  const currentUser = useCurrentUser();
  const canExportData = currentUser?.recognitionPermissions?.includes('exportData') ?? false;

  // "Confirmar Ganadores" (approveNominations) gatea confirmar un ganador pendiente, aparte de
  // "Gestionar ganadores" (manageWinners), que ya autoriza el resto de la pantalla (alta, edición,
  // borrado, carga). Mismo permiso que gatea el bloque de Aprobaciones de Campañas > Excepciones
  // (CampaignExceptions.tsx) — un solo permiso para ambas pantallas. AdminGlobal explícito, igual
  // que allá: no pasa por la matriz de permisos granulares.
  const canConfirmWinners = (currentUser?.roles?.includes('AdminGlobal') ?? false)
    || (currentUser?.recognitionPermissions?.includes('approveNominations') ?? false);

  // ── Filtros (todos van al servidor) ───────────────────────────────────────
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [anio, setAnio] = useState<string>('');
  const [ouId, setOuId] = useState('');
  const [country, setCountry] = useState('');
  const [fundamentalId, setFundamentalId] = useState('');
  /** CPE-134 — el eje que hace usable el flujo «sube → confirmas»: aislar lo pendiente. */
  const [estado, setEstado] = useState<'' | WinnerEstado>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_INICIAL);

  // El buscador NO dispara una petición por tecla: con el histórico de tres años detrás, teclear un
  // nombre lanzaría una consulta por letra y la última en llegar no tiene por qué ser la última
  // escrita.
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Cualquier cambio de filtro vuelve a la página 1: quedarse en la 4 de un resultado que ahora tiene
  // dos páginas muestra una tabla vacía que parece un error.
  useEffect(
    () => setPage(1),
    [qDebounced, anio, ouId, country, fundamentalId, estado, pageSize],
  );

  // ── Catálogos de los filtros (WP-D1) ──────────────────────────────────────
  //
  // 🔴 Hasta ahora OU, País y Fundamental eran CAJAS DE TEXTO LIBRE: había que saberse el
  // identificador interno y teclearlo. Escribir «México» no devolvía nada, y la pantalla no daba
  // ninguna pista de por qué. Es el mismo problema que hizo descartar la plantilla de 13 columnas:
  // pedirle a un administrador identificadores que no puede conocer.
  //
  // 🔴 Y CADA FILTRO ESPERA UN VALOR DISTINTO. Verificado en `WinnerRepository` antes de escribir
  // esto, porque mandar el valor equivocado deja el filtro devolviendo cero EN SILENCIO, que es peor
  // que la caja de texto:
  //   · OU          → `F.Eq(w => w.OuId, filtro.OuId)`            ⇒ se manda el **id** de la OU.
  //   · Fundamental → `F.Eq(w => w.FundamentalId, ...)`           ⇒ se manda el **id**.
  //   · País        → normaliza y compara contra `CountryIso` O `Country` ⇒ el **ISO alpha-2** vale.
  //
  // El sexto filtro (BU) se retiró (2026-09-03): el servidor nunca devolvía resultados con él puesto
  // —comparaba contra `Winner.Area`, que no queda poblado de forma confiable— y un filtro que
  // silenciosamente vacía la tabla es peor que no tenerlo. Ver commit de este cambio si se retoma.
  const ouCatalog = useRegistrationCatalogs();
  const fundCatalog = useRecognitionCatalog();
  // Task 9 — mismo catálogo que ya usa la hoja "Todos los reconocimientos" de Brillamos Juntos
  // (ShineTab.tsx), para que el header agrupado por Fundamental salga con los mismos nombres/orden.
  //
  // Fix round 1, hallazgo Important — `isLoading`/`isError` gatean el botón "Exportar nominaciones"
  // (más abajo): sin `catalogoCargando`, un clic mientras la query del catálogo aún no resuelve
  // arma `construirHojaExcelNominaciones` con `fundamentalesConHabitos` vacío (`catalogoReporteria.
  // data` en `undefined`), y el Excel sale con la matriz de hábitos en CERO columnas y CERO merges,
  // en silencio — pérdida de datos sin ningún aviso. Sin `catalogoConError`, el mismo riesgo se
  // repite cuando la query FALLA en vez de tardar: `isLoading` cae a `false` con `data` en
  // `undefined` igual que si nunca hubiera cargado, así que sin este segundo guard el botón se
  // reactivaba con el catálogo vacío y el archivo exportado quedaba con datos afirmativamente
  // incompletos, no solo con un hueco documentado. Mismo defecto ya encontrado y corregido para
  // "Todos los reconocimientos" en `ShineTab.tsx` (mismo hook, mismo riesgo).
  const {
    data: catalogoReporteria, isLoading: catalogoCargando, isError: catalogoConError,
  } = useFundamentalsForReporting();
  const countryCatalog = useCountryLanguages();
  // D4 — el catálogo que ESTA pantalla puede leer. `useAdminBadges` exige rol AdminGlobal y aquí
  // entran Admin OU/BU y Admin Local País por capacidad: les daría 403 y un selector vacío.
  //
  // 🔴 SIN filtrar por categoría (Streak + Campaign): un ganador real puede tener otorgada una
  // insignia de RACHA, no solo de campaña, y la columna necesita resolver su nombre igual. Hallazgo
  // Important de la revisión final — antes se pedía `useWinnerBadges()` acá Y en el selector/grid de
  // abajo, y el backend solo devolvía Campaign: un ganador con insignia de racha mostraba el id
  // crudo en vez del nombre.
  const badgeCatalog = useWinnerBadges();

  // El selector de asignación y "Insignias de Campaña" (grid + modal, más abajo) SÍ deben quedar
  // acotados a Campaign: ninguno de los dos debe poder ofrecer o administrar una insignia de racha.
  const campaignBadgeCatalog = useCampaignBadges();

  /** Nombre visible de una insignia otorgada, para la columna (catálogo completo, ver arriba). */
  const nombreInsignia = (id: string | null | undefined) => {
    if (!id) return null;
    const b = (badgeCatalog.data ?? []).find(
      (x) => x.id === id || x.code === id,
    );
    return b ? getBadgeName(b, i18n.language) : id;
  };

  /**
   * Año a mostrar/exportar de un ganador. `w.anio` es la fuente normal (la importación vigente lo
   * deriva de la campaña; la histórica, desde este fix, del año de la insignia). Cuando viene vacío
   * —ganadores históricos cargados ANTES de este fix, que el backend no migra solos— se resuelve
   * aquí igual que lo hace el backend: del año NUMÉRICO de la insignia otorgada (`badge.anio`),
   * NUNCA de `anioSubtitulo`, que es el rótulo de texto libre ×7 idiomas y puede no ser un año
   * ("Innovación LATAM"). Sin insignia o sin año en su ficha, no hay de dónde más tomarlo.
   */
  const anioGanador = (winner: WinnerRowDto): number | null => {
    if (winner.anio != null) return winner.anio;
    if (!winner.badgeId) return null;
    const insignia = (badgeCatalog.data ?? []).find(
      (item) => item.id === winner.badgeId || item.code === winner.badgeId,
    );
    return insignia?.anio ?? null;
  };

  /** Mapa de fundamentales por id para la columna de Fundamental. */
  const fundamentalPorId = useMemo(
    () => new Map((fundCatalog.data ?? []).map((f) => [f.id, f])),
    [fundCatalog.data],
  );

  // ── D3 · exportar la tabla filtrada ───────────────────────────────────────
  const [exportando, setExportando] = useState(false);
  const [exportTruncado, setExportTruncado] = useState(false);
  const [exportError, setExportError] = useState(false);
  // Defensa en profundidad — el botón ya se deshabilita en cliente cuando `total === 0`, pero ese
  // `total` es un snapshot que puede quedar obsoleto entre el render y el clic: el servidor puede
  // responder 0 filas reales al momento de exportar. Mismo patrón que `exportNominacionesVacio`.
  const [exportVacio, setExportVacio] = useState(false);

  const paises = useMemo(
    () => Object.keys(countryCatalog.data?.countries ?? {}).sort((a, b) => a.localeCompare(b)),
    [countryCatalog.data],
  );

  // Se guarda aparte de `params` porque también hace falta para calcular el `colorIndex` del avatar
  // de cada fila (posición absoluta en el listado, no solo en la página visible).
  const pageSizeEfectivo = pageSizeDeServidor(pageSize);

  const params: WinnerSearchParams = useMemo(() => ({
    q: qDebounced || undefined,
    anio: anio ? Number(anio) : undefined,
    ouId: ouId || undefined,
    country: country || undefined,
    fundamentalId: fundamentalId || undefined,
    estado: estado || undefined,
    page,
    pageSize: pageSizeEfectivo,
  }), [qDebounced, anio, ouId, country, fundamentalId, estado, page, pageSizeEfectivo]);

  const listado = useWinnersSearch(params);
  const deleteM = useDeleteWinner();
  const confirmM = useConfirmWinner();
  const updateM = useUpdateWinner();

  // Doble confirmación (D-7): borrar destruye un hecho ya publicado y no hay nada que deshacer.
  // `deleteStep` distingue el primer paso (nombre del ganador) del segundo (advertencia genérica),
  // ambos sobre el mismo `ConfirmModal` estilizado — antes eran dos `window.confirm()` nativos, que
  // no comparten el estilo del resto de los modales de confirmación de la app.
  const [deleteTarget, setDeleteTarget] = useState<WinnerRowDto | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  // 🔴 El botón de aceptar de `ConfirmModal` SIEMPRE llama `onConfirm()` y LUEGO `onClose()` — está
  // pensado para un solo paso, donde confirmar y cerrar son la misma acción. Aquí el paso 1 no debe
  // cerrar nada: confirmar solo AVANZA a `deleteStep=2`, y el `onClose` automático que sigue vaciaba
  // `deleteTarget`, así que el paso 2 (cuya condición de render exige `deleteTarget`) nunca llegaba a
  // montarse — la insignia visual se cerraba y `deleteM.mutate` no se llamaba jamás. La bandera le
  // dice a ESE `onClose` automático que se ignore a sí mismo: se marca de forma síncrona dentro de
  // `onConfirm`, en el mismo click, así que el `onClose` que corre justo después ya la ve puesta.
  // Un cierre real (Cancelar o clic en el fondo) nunca pasa por `onConfirm`, así que nunca la marca.
  const skipStep1CloseRef = useRef(false);

  // ── WP-D1 · Confirmación EN LOTE ──────────────────────────────────────────
  //
  // Un padrón real trae cientos de filas y todas nacen `Pendiente`. Confirmarlas de una en una,
  // con un clic por fila y esperando la respuesta de cada una, no es una molestia: es lo que hace
  // que el flujo «sube la plantilla → confirmas» no se pueda terminar.
  //
  // La selección se guarda por ID y NO se limpia al cambiar de página a propósito: el administrador
  // suele filtrar por Pendiente, recorrer varias páginas marcando, y confirmar al final. Perder lo
  // marcado al pasar de página convertiría el lote en una trampa.
  // 🔴 Guarda la FILA, no solo el id. Con un `Set<string>` no había forma de saber el estado de
  // los marcados en OTRAS páginas, así que el lote se limitaba a la página visible: el botón
  // decía «Confirmar 40» y confirmaba 20. Lo cazó la auditoría adversarial.
  const [seleccion, setSeleccion] = useState<ReadonlyMap<string, WinnerRowDto>>(new Map());
  const [confirmandoLote, setConfirmandoLote] = useState(false);
  const [erroresLote, setErroresLote] = useState<string[]>([]);

  const filas = listado.data?.filas ?? [];
  const total = listado.data?.total ?? 0;

  /** Solo las PENDIENTES son seleccionables: confirmar una ya confirmada no significa nada. */
  const pendientesEnPagina = filas.filter((w) => w.estado === 'Pendiente');
  const seleccionadasEnPagina = pendientesEnPagina.filter((w) => seleccion.has(w.id));
  const todasMarcadas = pendientesEnPagina.length > 0
    && seleccionadasEnPagina.length === pendientesEnPagina.length;

  const alternar = (w: WinnerRowDto) => setSeleccion((s) => {
    const n = new Map(s);
    if (n.has(w.id)) { n.delete(w.id); } else { n.set(w.id, w); }
    return n;
  });

  /** La casilla maestra actúa SOLO sobre la página visible: nadie puede marcar a ciegas 400 filas
   *  que no ha visto, y menos para una acción que dispara un aviso a cada ganador. */
  const alternarTodas = () => setSeleccion((s) => {
    const n = new Map(s);
    if (todasMarcadas) { pendientesEnPagina.forEach((w) => n.delete(w.id)); }
    else { pendientesEnPagina.forEach((w) => n.set(w.id, w)); }
    return n;
  });

  /**
   * Confirma las marcadas, UNA PETICIÓN POR GANADOR y en SERIE.
   *
   * 🔴 No hay endpoint de lote en el backend, y no se inventa aquí: cada confirmación publica el
   * aviso «eres ganador» al interesado (`WinnerAnnouncer`), así que no es una escritura idempotente
   * que se pueda reintentar a ciegas.
   *
   * En serie y no en paralelo por lo mismo: 300 peticiones simultáneas contra el servicio que además
   * encola 300 notificaciones es una forma rápida de tirar la cola de correo.
   *
   * Si una falla, las demás CONTINÚAN y al final se dice cuáles fallaron. Abortar dejaría el lote a
   * medias sin que el administrador sepa dónde se quedó.
   */
  const confirmarLote = async () => {
    // 🔴 Sobre TODA la selección, no sobre `filas`. Filtrar por la página visible era el defecto:
    // marcar en la página 1, pasar a la 2 y confirmar dejaba las primeras sin tocar mientras el botón
    // seguía prometiendo el total. Y es justo el flujo que el lote vino a resolver.
    const ids = [...seleccion.values()].filter((w) => w.estado === 'Pendiente').map((w) => w.id);
    if (ids.length === 0) return;
    setConfirmandoLote(true);
    // Un error de la tanda anterior no debe sobrevivir a una nueva: se limpia AL EMPEZAR, no al
    // acabar, o un lote correcto deja en pantalla el aviso rojo del anterior.
    setErroresLote([]);
    const fallidos: string[] = [];
    for (const id of ids) {
      try {
        await confirmM.mutateAsync(id);
        setSeleccion((s) => { const n = new Map(s); n.delete(id); return n; });
      } catch {
        fallidos.push(id);
      }
    }
    setErroresLote(fallidos);
    setConfirmandoLote(false);
  };

  /**
   * D3 — exporta la TABLA FILTRADA, no la página visible.
   *
   * 🔴 La tabla está paginada EN SERVIDOR (20 por página). Exportar `filas` daría 20 de 400 sin que
   * el administrador lo note, y un Excel incompleto que parece completo es peor que no tener botón:
   * las decisiones que se toman con él salen mal y nadie sabe por qué.
   *
   * Por eso se recorre el filtro pidiendo páginas. El servidor acota `pageSize` a 200
   * (`WinnerSearchFilter.MaxPageSize`), así que se piden lotes de ese tamaño — no vale con «pide
   * 100 000 y ya».
   *
   * ⚠️ Y hay un TOPE, con aviso. Sin él, un filtro vacío sobre tres años de histórico dispararía
   * decenas de peticiones y armaría un libro que el navegador no aguanta. Si se alcanza, se dice —
   * un truncamiento silencioso es exactamente el defecto que este bucle existe para evitar.
   */
  const exportar = async () => {
    setExportando(true);
    setExportTruncado(false);
    setExportError(false);
    setExportVacio(false);
    try {
      const LOTE = 200;
      const TOPE = 5000;
      const todas: WinnerRowDto[] = [];
      let pagina = 1;
      let total = 0;

      do {
        const res = await winnersApi.search({ ...params, page: pagina, pageSize: LOTE });
        // 🔴 Una página VACÍA corta el bucle, aunque el total diga que faltan filas.
        //
        // Sin esto el bucle no termina nunca: `todas` no crece, `todas.length < total` sigue siendo
        // cierto, y el navegador se queda pidiendo páginas para siempre contra el servidor. Y no es
        // hipotético — pasa si los datos cambian entre dos peticiones (alguien confirma o borra
        // mientras se exporta) o si el total y las filas se desincronizan por cualquier motivo.
        //
        // Lo encontró la auditoría adversarial. Un cierre por número de páginas no bastaría: el
        // síntoma es que la condición de avance deja de avanzar, no que sean muchas.
        if (res.filas.length === 0) break;
        todas.push(...res.filas);
        total = res.total;
        pagina += 1;
      } while (todas.length < total && todas.length < TOPE);

      if (todas.length < total) setExportTruncado(true);

      // El disabled del botón (`total === 0`) es un snapshot que puede quedar obsoleto entre el
      // render y el clic. Si el recorrido termina sin filas, se AVISA en vez de generar un Excel
      // de una sola fila de encabezados que parece un fallo sin serlo.
      if (todas.length === 0) {
        setExportVacio(true);
        return;
      }

      await exportToExcel(
        todas.map((w) => ({
          [t('admin.winners.colUser')]: w.nombre,
          [t('admin.winners.colEmail')]: w.email ?? '',
          [t('admin.winners.colPuesto')]: w.area ?? '',
          [t('admin.winners.colOu')]: w.ouName ?? w.ouId ?? '',
          [t('admin.winners.colDepto')]: w.siteName ?? '',
          [t('admin.winners.colCountry')]: w.country ?? '',
          [t('admin.winners.colFundamental')]: w.fundamentalId ?? '',
          [t('admin.winners.colYear')]: anioGanador(w) ?? '',
          [t('admin.winners.colBadge')]: nombreInsignia(w.badgeId) ?? '',
          [t('admin.winners.colStatus')]: w.estado === 'Confirmado'
            ? t('admin.winners.statusConfirmed')
            : t('admin.winners.statusPending'),
        })),
        `ganadores-${new Date().toISOString().slice(0, 10)}`,
      );
    } catch {
      // Si falla, se DICE. Con solo el `finally`, el botón se rehabilitaba, no aparecía ningún
      // fichero y el administrador no tenía forma de saber si había descargado algo o no.
      setExportError(true);
    } finally {
      setExportando(false);
    }
  };

  // ── Task 4.5 · exportar la data CRUDA de nominaciones ─────────────────────
  const [exportandoNominaciones, setExportandoNominaciones] = useState(false);
  // Estado de truncamiento APARTE del de `exportar()` (D3): este export no tiene filtros que afinar
  // —solo campaña—, así que reusar `exportTruncated` («Afina los filtros…») sería un aviso que no
  // aplica a este flujo.
  const [exportNominacionesTruncado, setExportNominacionesTruncado] = useState(false);
  // Ronda de revisión — cero filas (campaña sin nominaciones, o `NoAlcanzaNada` del backend) no
  // debe descargar un Excel vacío: se AVISA en vez de generar un fichero que parece un fallo.
  const [exportNominacionesVacio, setExportNominacionesVacio] = useState(false);

  /**
   * Task 4.5 — pide `GET v1/winners/nominations-export` (Task 4.4), NO la tabla de ganadores ya
   * cargada: es el libro crudo que el comité usa para elegir ganadores, no el resultado de esa
   * elección.
   *
   * Exige campaña elegida (el botón se deshabilita sin ella, igual que "Cargar Excel"): el endpoint
   * real no tiene modo "todas las campañas" (`WinnersController.GetNominationsExport` recibe
   * `campaignId` sin default).
   *
   * Mismo patrón de paginado-con-tope que `exportar()`, con UNA diferencia deliberada y crítica:
   * el corte es SOLO por página VACÍA o por el tope duro (5000 filas) — NUNCA comparando filas
   * acumuladas contra `res.total`. `total` cuenta NOMINACIONES (confirmado en
   * `GetNominationsRawExportQueryHandler`), y una nominación grupal aporta una fila POR CANDIDATO:
   * `todas.length` (filas) puede alcanzar o superar `total` (nominaciones) mucho antes de que se
   * hayan pedido todas las páginas. Comparar esas dos unidades —como sí es válido en `exportar()`,
   * donde `total` cuenta filas de ganadores— cortaba el recorrido a medias en cualquier campaña con
   * nominaciones grupales. Lo cazó la ronda de revisión adversarial.
   */
  const exportarNominacionesCrudas = async () => {
    if (!campanaDeCarga) return;
    setExportandoNominaciones(true);
    setExportNominacionesTruncado(false);
    setExportNominacionesVacio(false);
    setExportError(false);
    try {
      // Tarea 17 fix round 1 — el paginado (LOTE/TOPE, corte por página vacía) se extrajo a
      // `fetchNominacionesCrudasPaginado` (`../fetchNominacionesCrudas.ts`): vivía duplicado
      // verbatim aquí y en `ShineTab.tsx`.
      const { filas: todas, truncado } = await fetchNominacionesCrudasPaginado(campanaDeCarga);

      if (truncado) setExportNominacionesTruncado(true);

      if (todas.length === 0) {
        setExportNominacionesVacio(true);
        return;
      }

      // Fix crítico (revisión de rama completa, 2026-08-31) — a diferencia de "Todos los
      // reconocimientos" (Brillamos Juntos, `ShineTab.tsx`), este export es la fuente cruda que el
      // comité usa para decidir premios: no puede perder en silencio el hábito de una nominación solo
      // porque ese Hábito se desactivó DESPUÉS de que se citara. Se resuelve la matriz con la UNIÓN
      // (catálogo activo + hábitos retirados citados en `todas`) en vez de la variante activo-solamente
      // (ver `resolverFundamentalesConHabitosParaExport`, `fundamentalesHabitosMatrix.ts`).
      const fundamentalesConHabitosDelExport = resolverFundamentalesConHabitosParaExport(
        catalogoReporteria, i18n.language, todas, t('admin.winners.nominationsExport.retiredHabitsGroup'),
      );

      await exportMultiSheetExcel({
        fileName: `nominaciones-${new Date().toISOString().slice(0, 10)}`,
        sheets: [construirHojaExcelNominaciones(todas, fundamentalesConHabitosDelExport, t)],
      });
    } catch {
      setExportError(true);
    } finally {
      setExportandoNominaciones(false);
    }
  };

  /** La fila que se está editando (WP-D1 punto 12). `null` = el cajón está cerrado. */
  const [editando, setEditando] = useState<(RegisterWinnerBody & { id: string }) | null>(null);

  // ── Insignias de Campaña (Fase 2) ─────────────────────────────────────────
  //
  // A diferencia del cajón de edición de arriba, cada acción persiste al instante contra su propio
  // endpoint (Task 2.3): no hay un "Guardar" de sección que agrupe crear/editar/borrar/subir imagen.
  const [editandoInsignia, setEditandoInsignia] = useState<AdminBadgeDto | null>(null);
  const [insigniaEsNueva, setInsigniaEsNueva] = useState(false);
  const createBadge = useCreateCampaignBadge();
  const updateBadge = useUpdateCampaignBadge();
  const deleteBadge = useDeleteCampaignBadge();

  // Ronda de revisión — ninguna de las tres mutaciones se reseteaba nunca: el error de UNA se
  // arrastraba en la condición del banner incluso después de que OTRA distinta tuviera éxito (ej.
  // falla borrar, luego se crea una insignia con éxito, y el banner seguía mostrando el error viejo
  // del borrado). Se resetean las tres al abrir el editor (crear o editar): es el momento en que el
  // administrador vuelve a actuar sobre el catálogo, y ningún aviso de un intento anterior debería
  // sobrevivir a partir de ahí.
  const abrirNuevaInsignia = () => {
    createBadge.reset();
    updateBadge.reset();
    deleteBadge.reset();
    setInsigniaEsNueva(true);
    setEditandoInsignia({
      id: crypto.randomUUID(), code: crypto.randomUUID(), nombre: {}, anioSubtitulo: {}, anio: null,
      isActive: true, categoria: 'Campaign',
    });
  };

  const abrirEdicionInsignia = (badgeItem: AdminBadgeDto) => {
    createBadge.reset();
    updateBadge.reset();
    deleteBadge.reset();
    setInsigniaEsNueva(false);
    setEditandoInsignia(badgeItem);
  };

  const guardarInsignia = (draft: AdminBadgeDto) => {
    const accion = insigniaEsNueva
      ? createBadge.mutateAsync(draft)
      : updateBadge.mutateAsync({ id: draft.id, badge: draft });
    accion.then(() => setEditandoInsignia(null)).catch((error: unknown) => {
      // El modal se queda abierto: si falló (ej. faltan idiomas obligatorios en el backend), el
      // administrador no debe perder lo que ya tecleó. El mensaje visible vive DENTRO del modal
      // (prop `saveError` más abajo), no en un banner de página: el overlay del modal lo taparía.
      if (import.meta.env.DEV) console.error(error);
    });
  };

  const insigniaMutacionActiva = insigniaEsNueva ? createBadge : updateBadge;
  const insigniaGuardadoError = insigniaGuardadoErrorMensaje(insigniaMutacionActiva, t('admin.saveError'));

  // Task 0.2 (WP-D1) — el toggle ya NO es un `useState` de adorno: lee y escribe
  // `GestionSettingsDto.winnersEnabled` (`GET/PUT v1/admin/settings`), el mismo documento único que
  // gobierna el resto de Gestión. `?? true` reproduce el default del backend
  // (`GamificationRepository.GetGestionSettingsAsync`) para el documento legado que aún no lo trae.
  const gestionQ = useGestionSettings();
  const saveGestion = useSaveGestionSettings();
  const moduloActivo = gestionQ.data?.winnersEnabled ?? true;
  // El aviso solo se enciende con la ACCIÓN de apagar en esta sesión, no al cargar la pantalla con el
  // módulo ya apagado — igual que antes de esta tarea, cuando el `useState` local partía siempre en
  // `false`. Ampliar esto a "muéstralo también al cargar ya apagado" es una mejora de UX aparte, no
  // parte de conectar el toggle al backend.
  const [avisoApagado, setAvisoApagado] = useState(false);
  /** Fase 3 — modal de solo lectura que explica "vigente" vs "histórica" (Task 13). */
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  /**
   * D-10 — la campaña a la que pertenece el padrón que se va a subir.
   *
   * Va aquí y no en el fichero porque las seis columnas de la maqueta no la traen y
   * `Winner.CampaignId` es parte de la identidad de negocio: elegir mal la campaña no es un
   * detalle de etiquetado, cambia qué filas se rechazan.
   */
  const [campanaDeCarga, setCampanaDeCarga] = useState('');
  const campanas = useCampaigns(undefined, 1, 100);

  /** Task 14 — el asistente (WinnersImportWizardModal) que agrupa el toggle Vigente/Histórico, la
   *  plantilla y la carga del archivo, con validación previa antes de comprometer nada. */
  const [cargaAbierta, setCargaAbierta] = useState(false);

  // No hay columna de fecha: la maqueta muestra el AÑO del premio, que es un dato distinto de la
  // fecha de carga y el que de verdad importa aquí. `registradoAt` viaja igualmente porque el
  // backend lo necesita para la ventana de edición, pero no se pinta.

  /**
   * WP-D1 punto 12 — abre el formulario de edición sobre una fila.
   *
   * El cajón se rellena con lo que la fila ya tiene: el `PUT` conserva lo que no se le manda, pero
   * enseñar campos vacíos haría creer al administrador que está borrando datos.
   */
  const abrirEdicion = (w: WinnerRowDto) => {
    setEditando({
      id: w.id,
      campaignId: w.campaignId,
      userId: w.userId,
      nombre: w.nombre,
      ouId: w.ouId ?? null,
      ouName: w.ouName ?? null,
      country: w.country ?? null,
      badgeId: w.badgeId ?? null,
      posicion: w.posicion ?? null,
      origenCruzado: false,
      anio: w.anio ?? null,
      fundamentalId: w.fundamentalId ?? null,
      area: w.area ?? null,
      siteName: w.siteName ?? null,
    });
  };

  const guardarEdicion = () => {
    if (!editando) return;
    const { id, ...body } = editando;
    updateM.mutate({ id, body }, { onSuccess: () => setEditando(null) });
  };

  // El backend además exige Admin General — el doble paso de abajo es la cortesía, no el control.
  const eliminar = (w: WinnerRowDto) => {
    setDeleteTarget(w);
    setDeleteStep(1);
  };

  return (
    <AdminSection title={t('admin.winners.title')} description={t('admin.winners.descLong')}>
      {/* Toggle del módulo (CPE-129) */}
      <div className="adm-toggle-card">
        <div className="adm-toggle-card__info">
          <i className="fas fa-trophy" aria-hidden />
          <div>
            <strong>{t('admin.winners.moduleTitle')}</strong>
            <p>{t('admin.winners.moduleHint')}</p>
          </div>
        </div>
        <label className="switch" title={t('admin.winners.moduleHint')}>
          <input
            type="checkbox"
            checked={moduloActivo}
            aria-label={t('admin.winners.moduleTitle')}
            onChange={(e) => {
              const siguiente = e.target.checked;
              setAvisoApagado(!siguiente);
              // Reemplazo atómico (PUT v1/admin/settings, WP-D1): se manda el documento entero que
              // ya se tiene cargado con el único campo tocado, no un parche — el mismo patrón que
              // `AdminGestion.patch`. Sin `gestionQ.data` (todavía cargando) no hay nada que mandar.
              if (gestionQ.data) {
                saveGestion.mutate({ ...gestionQ.data, winnersEnabled: siguiente });
              }
            }}
          />
          <span className="slider" />
        </label>
      </div>
      {avisoApagado && (
        <p className="adm-warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.moduleOffWarn')}
        </p>
      )}
      {/* Ronda de revisión — antes el PUT fallido quedaba en silencio: el switch no tenía estado
          optimista propio (`checked` deriva 100 % de `gestionQ.data`, así que un guardado que falla
          nunca llega a "verse" aplicado), pero tampoco había NADA que le dijera al administrador que
          el clic no sirvió de nada. Mismo patrón que `deleteM.isError`/`confirmM.isError` más abajo. */}
      {saveGestion.isError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
          {getRecognitionErrorMessage(saveGestion.error, t('admin.winners.moduleSaveError'))}
        </p>
      )}

      {/* Header + 3 botones */}
      <div className="adm-toolbar">
        <h3 className="adm-toolbar__title">{t('admin.winners.manageTitle')}</h3>
        <span className="adm-spacer" />
        {/* D-10 — la campaña del padrón. Sin ella no se puede cargar: es parte de la identidad de
            cada ganador. */}
        <label className="adm-sr-only" htmlFor="gan-campana-carga">
          {t('admin.winners.uploadCampaign')}
        </label>
        <select
          id="gan-campana-carga"
          className="adm-select"
          value={campanaDeCarga}
          onChange={(e) => setCampanaDeCarga(e.target.value)}
        >
          <option value="">{t('admin.winners.uploadCampaignPick')}</option>
          {(campanas.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>{getCampaignName(c, i18n.language)}</option>
          ))}
        </select>
        {/* Task 14 — un solo botón abre el modal de carga: el toggle Vigente/Histórico, la
            plantilla y el input de archivo viven ahora ahí dentro (ver el modal más abajo). Nunca se
            deshabilita: el modal es quien explica, con avisos, qué falta elegir antes de subir —
            deshabilitarlo aquí solo escondería la puerta de entrada sin decir por qué. */}
        <button
          type="button"
          className="btn btn--primary"
          title={t('admin.winners.uploadOpenHint')}
          onClick={() => setCargaAbierta(true)}
        >
          <i className="fas fa-upload" aria-hidden /> {t('admin.winners.uploadOpen')}
        </button>
        {/* D3 — el botón dejó de estar muerto. Exporta la TABLA FILTRADA (todas sus páginas, no la
            visible). Se deshabilita mientras descarga y cuando no hay nada que exportar: ofrecer una
            descarga de cero filas produce un Excel vacío que parece un fallo.
            Task 4.5 — renombrado de `exportNominations` a `exportWinners`: este botón SIEMPRE exportó
            la tabla de GANADORES ya cargada, nunca las nominaciones. La etiqueta ahora dice lo que
            hace; el comportamiento no cambia. */}
        {canExportData && (
          <button
            type="button"
            className="btn btn--success"
            disabled={exportando || total === 0}
            title={total === 0 ? t('admin.winners.exportEmpty') : t('admin.winners.exportFiltered')}
            onClick={exportar}
          >
            <i className={exportando ? 'fas fa-spinner fa-spin' : 'fas fa-download'} aria-hidden />{' '}
            {exportando ? t('admin.winners.exporting') : t('admin.winners.exportWinners')}
          </button>
        )}
        {/* Task 4.5 — el botón REAL de "Exportar nominaciones": pide la data cruda al backend
            (`GET v1/winners/nominations-export`, Task 4.4), no la tabla de arriba. Exige campaña
            elegida porque el endpoint no tiene modo "todas las campañas". */}
        {canExportData && (
          <button
            type="button"
            className="btn btn--success"
            // Task 9, fix round 1 — `catalogoCargando` (sin el catálogo de Fundamentales/Hábitos
            // cargado, `construirHojaExcelNominaciones` armaría la matriz de hábitos con CERO
            // columnas y CERO merges en silencio, un Excel con datos incompletos que parece
            // completo). `catalogoConError` — mismo gate para cuando la query del catálogo FALLÓ
            // (no solo tarda): sin esto el botón se reactivaba con el catálogo en `undefined` y el
            // export salía con la misma pérdida de datos, en vez de bloquearse. Mismo defecto ya
            // encontrado y corregido para "Todos los reconocimientos" en `ShineTab.tsx` (mismo hook).
            disabled={exportandoNominaciones || !campanaDeCarga || catalogoCargando || catalogoConError}
            // Fix (revisión de rama completa, 2026-08-31) — con `catalogoConError` el botón ya
            // quedaba deshabilitado, pero sin ningún `title` que lo explicara: se veía simplemente
            // roto. La carga (`catalogoCargando`) es transitoria y se entiende sola; el error no.
            title={
              catalogoConError
                ? t('admin.winners.exportNominationsCatalogError')
                : campanaDeCarga ? undefined : t('admin.winners.pickCampaignFirst')
            }
            onClick={exportarNominacionesCrudas}
          >
            <i className={exportandoNominaciones ? 'fas fa-spinner fa-spin' : 'fas fa-download'} aria-hidden />{' '}
            {exportandoNominaciones ? t('admin.winners.exporting') : t('admin.winners.exportNominations')}
          </button>
        )}
        {/* Task 13 — ayuda de solo lectura: explica en lenguaje llano la diferencia entre "vigente"
            (ligada a campaña) e "histórica" (sin campaña, una sola insignia para el archivo entero). */}
        <button
          type="button"
          className="btn-icon btn-icon--outline"
          title={t('admin.winners.uploadOpenHint')}
          aria-label={t('admin.winners.helpOpen')}
          onClick={() => setAyudaAbierta(true)}
        >
          <i className="fas fa-circle-question" aria-hidden />
        </button>
      </div>

      {/* Task 14 — el panel de validación y el de templateError se retiraron de aquí: ahora viven
          DENTRO del modal de carga (más abajo), que es donde ocurre la acción que los dispara. */}

      <p className="adm-note">{t('admin.winners.flowHint')}</p>

      {/* Barra de filtros — TODO server-side. En < 768px se colapsa en un <details>: seis filtros
          en fila empujaban el listado varias pantallas hacia abajo (Task 1.4). CSS-only, sin
          estado de React nuevo — en escritorio el <summary> se oculta y arranca `open` en mobile. */}
      <details className="adm-filter-details" open>
        <summary className="adm-filter-summary">{t('admin.winners.filtersToggle')}</summary>
        <div className="adm-filter-bar">
          <div className="adm-filter-search">
            <i className="fas fa-search" aria-hidden />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.winners.searchByName')}
              aria-label={t('admin.winners.searchByName')}
            />
          </div>
          <label className="adm-sr-only" htmlFor="gan-filter-anio">{t('admin.winners.filterYear')}</label>
          <select
            id="gan-filter-anio"
            className="adm-filter-select"
            value={anio}
            onChange={(e) => setAnio(e.target.value)}
          >
            <option value="">{t('admin.winners.yearAll')}</option>
            {aniosDisponibles().map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {/* WP-D1 — los cuatro ejes pasan de caja de texto a DESPLEGABLE, como en la maqueta
              (`fundamentales-admin.js:331-335`). Cada uno manda el valor que su filtro compara de
              verdad; ver el comentario de los catálogos, arriba. */}
          <label className="adm-sr-only" htmlFor="gan-filter-ou">{t('admin.winners.filterOu')}</label>
          <select
            id="gan-filter-ou"
            className="adm-filter-select"
            value={ouId}
            onChange={(e) => setOuId(e.target.value)}
          >
            <option value="">{t('admin.winners.ouAll')}</option>
            {(ouCatalog.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
  
          <label className="adm-sr-only" htmlFor="gan-filter-pais">{t('admin.winners.filterCountry')}</label>
          <select
            id="gan-filter-pais"
            className="adm-filter-select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="">{t('admin.winners.countryAll')}</option>
            {paises.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
  
          <label className="adm-sr-only" htmlFor="gan-filter-fund">{t('admin.winners.filterFundamental')}</label>
          <select
            id="gan-filter-fund"
            className="adm-filter-select"
            value={fundamentalId}
            onChange={(e) => setFundamentalId(e.target.value)}
          >
            <option value="">{t('admin.winners.fundamentalAll')}</option>
            {(fundCatalog.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>{getFundamentalName(f, i18n.language)}</option>
            ))}
          </select>
  
          {/* CPE-134 — el eje de Estado. El backend lo implementa y el contrato lo declara; sin él, el
              flujo «sube la plantilla → se refleja aquí → confirmas» obliga a buscar a ojo las filas
              pendientes entre miles de históricas, que es justo el trabajo que la pantalla evita. */}
          <label className="adm-sr-only" htmlFor="gan-filter-estado">
            {t('admin.winners.filterEstado')}
          </label>
          <select
            id="gan-filter-estado"
            className="adm-filter-select"
            value={estado}
            onChange={(e) => setEstado(e.target.value as '' | WinnerEstado)}
          >
            <option value="">{t('admin.winners.estadoAll')}</option>
            <option value="Pendiente">{t('admin.winners.statusPending')}</option>
            <option value="Confirmado">{t('admin.winners.statusConfirmed')}</option>
          </select>
        </div>
      </details>

      {/* D3 — el truncamiento se DICE. Un Excel recortado en silencio parece completo, y las
          decisiones que se toman con él salen mal sin que nadie sepa por qué. */}
      {exportError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.exportError')}
        </p>
      )}

      {exportTruncado && (
        <p className="adm-note adm-note--warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.exportTruncated')}
        </p>
      )}

      {exportVacio && (
        <p className="adm-note adm-note--warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden /> {t('admin.winners.exportEmpty')}
        </p>
      )}

      {exportNominacionesTruncado && (
        <p className="adm-note adm-note--warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
          {t('admin.winners.exportNominationsTruncated')}
        </p>
      )}

      {exportNominacionesVacio && (
        <p className="adm-note adm-note--warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
          {t('admin.winners.exportNominationsEmpty')}
        </p>
      )}

      {/* Nota ámbar del periodo de edición */}
      <div className="adm-alert adm-alert--info" role="note">
        <i className="fas fa-info-circle" aria-hidden />
        <span>{t('admin.winners.editWindowNote')}</span>
      </div>

      {/* WP-D1 — barra de la confirmación en lote. Solo aparece cuando hay algo marcado: una barra
          permanente y vacía es ruido en una pantalla que ya tiene seis filtros. */}
      {seleccion.size > 0 && (
        <div className="adm-bulk-bar" role="region" aria-label={t('admin.winners.bulkTitle')}>
          <span className="adm-bulk-count">
            <i className="fas fa-check-double" aria-hidden />{' '}
            {t('admin.winners.bulkSelected', { count: seleccion.size })}
          </span>
          <button
            type="button"
            className="btn btn--primary"
            disabled={confirmandoLote}
            onClick={confirmarLote}
          >
            {confirmandoLote
              ? t('admin.winners.bulkConfirming')
              : t('admin.winners.bulkConfirm', { count: seleccion.size })}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={confirmandoLote}
            onClick={() => { setSeleccion(new Map()); setErroresLote([]); }}
          >
            {t('admin.winners.bulkClear')}
          </button>
        </div>
      )}

      {/* Se dice CUÁLES fallaron, no «hubo errores»: sin el detalle, el administrador no sabe qué
          reintentar y acaba confirmando otra vez todo el lote — y cada confirmación avisa al ganador. */}
      {erroresLote.length > 0 && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
          {t('admin.winners.bulkFailed', { count: erroresLote.length })}
        </p>
      )}

      {(deleteM.isError || confirmM.isError) && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
          {getRecognitionErrorMessage(
            deleteM.error ?? confirmM.error, t('admin.winners.actionError'))}
        </p>
      )}

      {deleteTarget && deleteStep === 1 && (
        <ConfirmModal
          title={t('admin.winners.deleteTitle')}
          message={t('admin.winners.confirmDelete1', { nombre: deleteTarget.nombre })}
          confirmText={t('admin.winners.deleteConfirm')}
          onConfirm={() => { skipStep1CloseRef.current = true; setDeleteStep(2); }}
          onClose={() => {
            if (skipStep1CloseRef.current) { skipStep1CloseRef.current = false; return; }
            setDeleteTarget(null);
          }}
        />
      )}
      {deleteTarget && deleteStep === 2 && (
        <ConfirmModal
          title={t('admin.winners.deleteTitle')}
          message={t('admin.winners.confirmDelete2')}
          confirmText={t('admin.winners.deleteConfirm')}
          onConfirm={() => { deleteM.mutate(deleteTarget.id); setDeleteTarget(null); }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      <StateBlock
        isLoading={listado.isLoading}
        isError={listado.isError}
        error={listado.error}
        isEmpty={filas.length === 0}
        emptyText={t('admin.winners.noResults')}
        onRetry={() => { listado.refetch(); }}
      >
        <div className="adm-table-wrap">
          <table className="adm-table adm-table--glass adm-table--cards">
            <thead>
              <tr>
                <th className="adm-th-check">
                  {/* `indeterminate` NO es un atributo de HTML ni de JSX: solo existe como propiedad
                      del nodo, por eso va por `ref`. Sin él, con 3 de 20 marcadas la casilla se ve
                      VACÍA — que es lo mismo que se ve con 0 marcadas, y con un lector de pantalla
                      se anuncia igual. */}
                  {canConfirmWinners && (
                    <input
                      type="checkbox"
                      ref={(el) => {
                        if (el) el.indeterminate = seleccionadasEnPagina.length > 0 && !todasMarcadas;
                      }}
                      checked={todasMarcadas}
                      disabled={pendientesEnPagina.length === 0}
                      onChange={alternarTodas}
                      aria-label={t('admin.winners.bulkSelectAll')}
                      title={t('admin.winners.bulkSelectAll')}
                    />
                  )}
                </th>
                <th>{t('admin.winners.colUser')}</th>
                {/* 🔴 Estas dos columnas NO son «Área» y «Site», aunque los campos del DTO se sigan
                    llamando `area`/`siteName`. Por decisión de negocio (03-sep-2026,
                    `SearchWinnersQueryHandler`) el backend dejó de resolverlas contra la BU y el
                    Site del padrón —vacíos o con el id crudo en las fichas Frontline— y las resuelve
                    contra el JobTitle y el Depto de la ficha: lo que llega es el PUESTO y el
                    DEPARTAMENTO de la persona. Los rótulos viejos describían el campo de origen, no
                    el dato que se pinta. Renombrar los campos del contrato es aparte. */}
                <th>{t('admin.winners.colPuesto')}</th>
                <th>{t('admin.winners.colOu')}</th>
                <th>{t('admin.winners.colDepto')}</th>
                <th>{t('admin.winners.colCountry')}</th>
                <th>{t('admin.winners.colFundamental')}</th>
                <th>{t('admin.winners.colYear')}</th>
                {/* D4 — la Insignia ocupa el sitio que la maqueta daba a «Puntos». No es un añadido
                    nuestro: el cliente quitó el puntaje y lo sustituyó por esta columna (nota de
                    Mariana, `08 - Ganadores.xlsx` → «Notas y Hallazgos» A14), y su plantilla de carga
                    ya trae «Insignia» y ninguna columna de puntaje. La maqueta no se actualizó. */}
                <th>{t('admin.winners.colBadge')}</th>
                {/*
                  🔴 La columna Puntos de la maqueta NO se pinta (D-5).

                  `Winner` tiene prohibido almacenar puntos (REQ-040) y no hay vía desde aquí al libro
                  de RulesEngine. Pintarla en cero afirmaría que valen 0, que es distinto de "no lo
                  sé" — y es exactamente el defecto que la columna "Total acumulado" ya tuvo. Se
                  oculta hasta que exista la vía.
                */}
                <th>{t('admin.winners.colStatus')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody id="ganadores-tbody">
              {filas.map((w, posicion) => (
                <tr key={w.id} className={seleccion.has(w.id) ? 'adm-row--selected' : undefined}>
                  <td className="adm-td-check" data-label="">
                    {/* Solo las pendientes: marcar una confirmada no significa nada, y ofrecerlo
                        sugiere que hace algo. Sin "Confirmar Ganadores" (approveNominations) no hay
                        acción posible al marcarla, así que tampoco se ofrece. */}
                    {w.estado === 'Pendiente' && canConfirmWinners && (
                      <input
                        type="checkbox"
                        checked={seleccion.has(w.id)}
                        onChange={() => alternar(w)}
                        aria-label={t('admin.winners.bulkSelectOne', { name: w.nombre })}
                      />
                    )}
                  </td>
                  <td data-label={t('admin.winners.colUser')}>
                    <div className="adm-user">
                      <InitialsAvatar
                        name={w.nombre}
                        baseClass="adm-avatar"
                        colorIndex={(page - 1) * pageSizeEfectivo + posicion}
                      />
                      <div>
                        <strong>{w.nombre}</strong>
                        {/* La maqueta pinta el CORREO bajo el nombre (`fundamentales-admin.js:341`),
                            que es lo que el administrador reconoce y, desde D-10, la llave de la
                            carga. Sin correo —ficha Frontline sin correo— cae a la NÓMINA (con nota
                            de qué es, para no confundirla con un correo o un id): es el identificador
                            equivalente que sí reconoce el padrón. Solo si tampoco hay nómina en el
                            directorio cae al `userId` crudo, para que la celda nunca quede vacía. */}
                        <span>
                          {w.email ?? (w.nomina ? t('admin.winners.colUserNomina', { nomina: w.nomina }) : w.userId)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td data-label={t('admin.winners.colPuesto')}>{w.area ?? '—'}</td>
                  <td data-label={t('admin.winners.colOu')}>{w.ouName ?? w.ouId ?? '—'}</td>
                  <td data-label={t('admin.winners.colDepto')}>{w.siteName ?? '—'}</td>
                  <td data-label={t('admin.winners.colCountry')}>{w.country ?? '—'}</td>
                  <td data-label={t('admin.winners.colFundamental')}>
                    {w.fundamentalId ? (() => {
                      const fund = fundamentalPorId.get(w.fundamentalId);
                      return (
                        <span className="adm-fund-cell">
                          <span
                            className="adm-fund-dot"
                            data-testid={`winner-fund-dot-${w.id}`}
                            style={{ background: fund?.color ?? 'var(--cp-purple)' }}
                          />
                          {fund ? getFundamentalName(fund, i18n.language) : w.fundamentalId}
                        </span>
                      );
                    })() : '—'}
                  </td>
                  <td data-label={t('admin.winners.colYear')}>{anioGanador(w) ?? '—'}</td>
                  <td data-label={t('admin.winners.colBadge')}>{nombreInsignia(w.badgeId) ?? '—'}</td>
                  <td data-label={t('admin.winners.colStatus')}>
                    <span
                      className={`adm-badge ${w.estado === 'Confirmado' ? 'adm-badge--ok' : 'adm-badge--warn'}`}
                    >
                      {w.estado === 'Confirmado'
                        ? t('admin.winners.statusConfirmed')
                        : t('admin.winners.statusPending')}
                    </span>
                  </td>
                  <td data-label={t('common.actions')}>
                    <div className="adm-actions">
                      {w.estado === 'Pendiente' && canConfirmWinners && (
                        <button
                          type="button"
                          className="btn-icon btn-icon--outline"
                          title={t('admin.winners.confirmAction')}
                          aria-label={t('admin.winners.confirmAction')}
                          disabled={confirmM.isPending}
                          onClick={() => confirmM.mutate(w.id)}
                        >
                          <i className="fas fa-check" aria-hidden />
                        </button>
                      )}
                      {w.editable && (
                        <>
                          <button
                            type="button"
                            className="btn-icon btn-icon--outline"
                            title={t('admin.winners.editAction')}
                            aria-label={t('admin.winners.editAction')}
                            onClick={() => abrirEdicion(w)}
                          >
                            <i className="fas fa-pen" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-icon--outline danger"
                            title={t('common.remove')}
                            aria-label={t('common.remove')}
                            disabled={deleteM.isPending}
                            onClick={() => eliminar(w)}
                          >
                            <i className="fas fa-trash" aria-hidden />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </StateBlock>

      {/*
        🔴 El paginador va FUERA del `StateBlock`, y no es un detalle de maquetación.
        Dentro, `isEmpty` sustituye a TODOS sus hijos por el texto de "sin resultados": quien estaba
        en la página 4 y aplicaba un filtro que devuelve cero se quedaba sin ningún control para
        volver — la pantalla parecía rota y solo se salía recargando.
        Se oculta solo cuando no hay nada cargado NI paginado (primera carga o error), donde un
        paginador de "página 1 de 1" es ruido.
      */}
      {!listado.isLoading && !listado.isError && (
        <AdminPagerFooter
          total={total}
          page={page}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={(size) => { setPageSize(size); setPage(1); }}
        />
      )}

      {/* Insignias de Campaña (Fase 2) — grid + modal de edición. `campaignBadgeCatalog` ya existe
          arriba (`useCampaignBadges()`), acotado a `categoria=Campaign` server-side: este catálogo
          administrable nunca debe ofrecer una insignia de racha. */}
      <AdminCampaignBadgeGrid
        badges={campaignBadgeCatalog.data ?? []}
        onAdd={abrirNuevaInsignia}
        onEdit={abrirEdicionInsignia}
        onDelete={(badgeItem) => deleteBadge.mutate(badgeItem.id)}
      />
      {/* Solo el error de ELIMINAR va en un banner de página: es la única de las tres acciones que
          no deja ningún modal abierto (crear/editar sí, y el suyo vive DENTRO de
          AdminCampaignBadgeEditorModal — ver `saveError`, más abajo — porque un banner aquí quedaría
          tapado por el overlay del modal). */}
      {deleteBadge.isError && (
        <p className="adm-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
          {getRecognitionErrorMessage(deleteBadge.error, t('admin.saveError'))}
        </p>
      )}
      <AdminCampaignBadgeEditorModal
        badge={editandoInsignia}
        isNew={insigniaEsNueva}
        onSave={guardarInsignia}
        onIconUploaded={(updated) => setEditandoInsignia(updated)}
        onClose={() => setEditandoInsignia(null)}
        saveError={insigniaGuardadoError}
      />

      {/* Edición (punto 12) — en modal (mismo componente compartido que el resto del Admin:
          AdminBadgeEditorModal, AdminModeration, etc.), respetando el layout responsivo de
          `.adm-form-grid` que ya traía el formulario. */}
      {editando && (
        <Modal open onClose={() => setEditando(null)} maxWidth={720} labelledBy={WINNER_EDIT_TITLE_ID}>
          <ModalHeader onClose={() => setEditando(null)}>
            <h2 id={WINNER_EDIT_TITLE_ID}>{t('admin.winners.editTitle', { nombre: editando.nombre })}</h2>
          </ModalHeader>
          <ModalBody>
            {/*
              Task 17 — el formulario se recorta a Fundamental e Insignia: son los dos únicos datos
              que el negocio quiere que el admin pueda corregir aquí. Los otros siete campos
              (`nombre`, `ouName`, `country`, `posicion`, `anio`, `area`, `siteName`) SIGUEN viajando
              en `editando` con su valor original —los rellena `abrirEdicion` desde la fila— y
              `guardarEdicion` los sigue mandando tal cual en el `PUT`: quitarlos de aquí no los quita
              del payload, solo deja de ofrecer un `<input>` para tocarlos.
            */}
            <div className="adm-form-grid">
              {/* Fundamental pasa de `<input type="text">` (el admin escribía el id a mano) a
                  `<select className="field">` sobre `fundCatalog` —el mismo catálogo que ya alimenta el filtro de la
                  tabla— para no poder guardar un id que no exista. */}
              <label className="adm-field">
                <span>{t('admin.winners.colFundamental')}</span>
                <select className="field"
                  id="adm-winner-edit-fundamental"
                  title={t('admin.winners.colFundamental')}
                  value={editando.fundamentalId ?? ''}
                  onChange={(e) => setEditando({ ...editando, fundamentalId: e.target.value || null })}
                >
                  <option value="">{t('admin.winners.fundamentalNone')}</option>
                  {(fundCatalog.data ?? []).map((f) => (
                    <option key={f.id} value={f.id}>{getFundamentalName(f, i18n.language)}</option>
                  ))}
                </select>
              </label>
              {/* D4 — el selector de insignia. Hasta ahora `badgeId` viajaba en el payload y NO había
                  forma de elegirla desde ninguna pantalla: el campo existía y era inalcanzable.
                  Se ofrece el catálogo de Campaña COMPLETO (`campaignBadgeCatalog`), inactivas
                  incluidas, porque un ganador de un año anterior puede llevar una que ya no se otorga
                  y quitársela al editar otra cosa sería perder un dato histórico sin querer.
                  Acotado a Campaign a propósito: una insignia de racha se otorga sola, nunca se asigna
                  a mano desde este selector. */}
              <label className="adm-field">
                <span>{t('admin.winners.colBadge')}</span>
                <select className="field"
                  title={t('admin.winners.colBadge')}
                  value={editando.badgeId ?? ''}
                  onChange={(e) => setEditando({ ...editando, badgeId: e.target.value || null })}
                >
                  <option value="">{t('admin.winners.badgeNone')}</option>
                  {(campaignBadgeCatalog.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>{getBadgeName(b, i18n.language)}</option>
                  ))}
                </select>
              </label>
            </div>

            {updateM.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden />{' '}
                {getRecognitionErrorMessage(updateM.error, t('admin.saveError'))}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn--secondary" onClick={() => setEditando(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!editando.nombre.trim() || updateM.isPending}
              onClick={guardarEdicion}
            >
              <i className="fas fa-floppy-disk" aria-hidden />{' '}
              {updateM.isPending ? t('admin.saving') : t('admin.winners.save')}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* Task 14 — asistente de carga (4 pasos: Instrucciones → Subir → Revisar → Confirmar), con
          validación previa (`preview: true`) antes de comprometer nada. La campaña se elige DENTRO
          del asistente (`onCampaignChange`) — antes solo vivía en la barra de herramientas y quedaba
          fuera de vista al abrir el modal, lo que confundía sobre para cuál campaña se estaba
          cargando. El modo "histórica" no usa campaña: elige su propia insignia dentro del asistente. */}
      {cargaAbierta && (
        <WinnersImportWizardModal
          onClose={() => setCargaAbierta(false)}
          campaignId={campanaDeCarga}
          campaigns={campanas.data?.items ?? []}
          onCampaignChange={setCampanaDeCarga}
          campaignBadges={campaignBadgeCatalog.data ?? []}
        />
      )}

      {/* Task 13 — modal de ayuda, de solo lectura: explica "vigente" vs "histórica" sin exigir
          que el administrador adivine la diferencia leyendo los dos selectores de la barra. */}
      {ayudaAbierta && (
        <Modal open onClose={() => setAyudaAbierta(false)} maxWidth={560} labelledBy="adm-winner-help-title">
          <ModalHeader onClose={() => setAyudaAbierta(false)}>
            <h2 id="adm-winner-help-title">{t('admin.winners.helpModalTitle')}</h2>
          </ModalHeader>
          <ModalBody>
            <p>{t('admin.winners.helpIntroVigente')}</p>
            <p>{t('admin.winners.helpIntroHistorico')}</p>
            <p>{t('admin.winners.helpDirectorio')}</p>
            <p>{t('admin.winners.helpNoDownload')}</p>
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn--secondary" onClick={() => setAyudaAbierta(false)}>
              {t('common.close')}
            </button>
          </ModalFooter>
        </Modal>
      )}

    </AdminSection>
  );
}

// Vive en `adminShared` desde la fila 40 (el editor de insignias ofrece el mismo rango); se
// re-exporta para no romper a quien ya la importaba desde aquí.
export { aniosDisponibles } from './adminShared';

export default AdminWinners;
